import { useMemo } from 'react';
import { ALL_FEATURES, SPECIAL_FEATURES } from '../../../../config/features';
import { useAuthContext } from '../../../../context/AuthContext';
import { useReservationTypes } from '../../../../hooks/AppData/useReservationTypes';
import { hasFeaturesPermission } from '../../../../utils/featureUtils';

type ReservationTypesValue = ReturnType<typeof useReservationTypes>;

export const useProfilePermissions = ({
  reservationTypes,
  reservationTypeFeatures,
}: {
  reservationTypes: ReservationTypesValue['reservationTypes'];
  reservationTypeFeatures: ReservationTypesValue['reservationTypeFeatures'];
}) => {
  const { features, featuresLoading } = useAuthContext();
  const userHasEditFeature = hasFeaturesPermission([ALL_FEATURES.EXTERNAL_USERS_WRITE], { features, featuresLoading });
  const userHasViewRoutinesFeature = hasFeaturesPermission([ALL_FEATURES.ROUTINES], { features, featuresLoading });
  const userHasCreateRoutinesFeature = hasFeaturesPermission([ALL_FEATURES.ROUTINES_WRITE], { features, featuresLoading });
  const userHasCreateFisioReservationsFeature = hasFeaturesPermission([SPECIAL_FEATURES.FISIO_RESERVATIONS_WRITE], {
    features,
    featuresLoading,
  });
  const hasViewFisioReservationsFeature = hasFeaturesPermission([SPECIAL_FEATURES.FISIO_RESERVATIONS_READ], {
    features,
    featuresLoading,
  });
  const hasAnyReservationTypeFeature = hasFeaturesPermission(reservationTypeFeatures ?? [], { features, featuresLoading });
  const reservationManageFeatures = useMemo(
    () => reservationTypes?.map((type) => type.features.manage).filter(Boolean) ?? [],
    [reservationTypes],
  );
  const canBulkCancelReservations = hasFeaturesPermission(reservationManageFeatures, { features, featuresLoading });

  return {
    userHasEditFeature,
    userHasViewRoutinesFeature,
    userHasCreateRoutinesFeature,
    userHasCreateFisioReservationsFeature,
    hasViewFisioReservationsFeature,
    hasAnyReservationTypeFeature,
    canBulkCancelReservations,
  };
};
